import React from 'react';
import '../components/grammar-page.css';
import { FaBookOpen, FaLightbulb } from 'react-icons/fa';

const PresentPerfectContinuousExplanation = () => {
  return (
    <div className="grammar-explanation">
      <h2><FaBookOpen /> Present Perfect Continuous</h2>

      <p>
        We use the <strong>present perfect continuous</strong> to talk about actions or situations that started in the past and are still continuing, or have recently stopped but have a connection to the present. It emphasizes the duration or ongoing nature of an action.
      </p>

      <div className="grammar-explanation-grid">
        <div className="example-block">
          <h3><FaLightbulb /> Form</h3>
          <ul>
            <li><strong>Affirmative:</strong> subject + have/has + been + verb-ing<br /><em>I have been studying for three hours.</em></li>
            <li><strong>Negative:</strong> subject + have/has + not been + verb-ing<br /><em>She hasn't been feeling well lately.</em></li>
            <li><strong>Question:</strong> have/has + subject + been + verb-ing<br /><em>Have you been waiting long?</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Usage: Actions Continuing to Present</h3>
          <ul>
            <li>Actions that started in the past and continue now<br /><em>I have been living in London for five years.</em> (I still live there)</li>
            <li>Temporary situations<br /><em>He has been working the night shift this week.</em> (temporary arrangement)</li>
            <li>Repeated actions over a period of time<br /><em>They have been visiting their grandparents every weekend.</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Usage: Recent Actions with Present Result</h3>
          <ul>
            <li>Actions that have just stopped but have a visible result<br /><em>Sorry I'm late! I have been stuck in traffic.</em></li>
            <li>Actions with evidence in the present<br /><em>Your eyes are red. Have you been crying?</em></li>
            <li>Actions where we focus on the activity, not the completion<br /><em>I have been cleaning the house all morning.</em> (focus on the cleaning activity)</li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Time Words: For and Since</h3>
          <ul>
            <li><strong>For</strong> = duration (period of time)<br /><em>I have been waiting for <strong>two hours</strong>.</em></li>
            <li><strong>Since</strong> = starting point<br /><em>She has been working here <strong>since January</strong>.</em></li>
            <li>Can also be used with: lately, recently, all day/all morning, etc.<br /><em>What have you been doing <strong>lately</strong>?</em></li>
          </ul>
        </div>
      </div>

      <div className="grammar-flex-table">
        {[
          ['Aspect', 'Present Perfect Simple', 'Present Perfect Continuous'],
          ['Focus', 'Completed action', 'Ongoing action'],
          ['Emphasis', 'Result or achievement', 'Duration or activity'],
          ['With stative verbs', '✅ Used (have known, have had)', '❌ Not used'],
          ['Example', 'I have lived here for 5 years. (focus on fact)', 'I have been living here for 5 years. (focus on experience)'],
        ].map((row, index) => (
          <div key={index} className={`grammar-flex-row${index === 0 ? ' header' : ''}`}>
            {row.map((cell, i) => (
              <div key={i} className="grammar-cell">{cell}</div>
            ))}
          </div>
        ))}
      </div>

      <div className="grammar-explanation-text">
        <h3><FaLightbulb /> Important Notes</h3>
        <ul>
          <li><strong>Stative verbs:</strong> We don't use present perfect continuous with stative verbs (know, believe, own, etc.). Use present perfect simple instead.<br /><em>I've known her for years.</em> (NOT: I've been knowing her)</li>
          <li><strong>Permanent vs temporary:</strong> Present perfect continuous often suggests temporary situations.<br /><em>I've been living with my parents (temporarily) while I look for a flat.</em></li>
          <li><strong>Length of time:</strong> We use it more often when we specify how long something has been happening.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
          <li>I <strong>have been waiting</strong> for you since 2 p.m.</li>
          <li>She <strong>has been working</strong> at that company for ten years.</li>
          <li>They <strong>have been traveling</strong> around Asia for the last six months.</li>
          <li>We <strong>have been looking</strong> for a new apartment but haven't found one yet.</li>
          <li>He <strong>has been feeling</strong> tired lately.</li>
        </ul>
      </div>
    </div>
  );
};

export default PresentPerfectContinuousExplanation;